import React from "react";
import {
  Alert,
  Box,
  Button,
  KeyValuePairs,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { OmsReport } from "../../lusca-search-api/generated-src";
import { reportTimestamp } from "./OMSReportSchedule";
import { fileSize } from "./omsReportFormatting";

export default function OMSExportSource({
  report,
  availableReport,
  accepted,
  onUseLatest,
  compact = false,
  showMetadata = true,
}: {
  report: OmsReport;
  availableReport: OmsReport;
  accepted: boolean;
  onUseLatest: () => void;
  compact?: boolean;
  showMetadata?: boolean;
}) {
  const newer = report.snapshotId !== availableReport.snapshotId;
  const heading =
    newer && accepted ? "Original source report" : "Source report";
  if (!showMetadata && !newer) return null;
  return (
    <section aria-label="Source report">
      <SpaceBetween size="s">
        {showMetadata && (
          <div className="oms-report-download__section-heading">
            <Box variant={compact ? "span" : "h3"}>
              {compact ? (
                <>
                  <strong>Data refreshed: </strong>
                  <time dateTime={report.sourceRefreshedAt}>
                    {reportTimestamp(report.sourceRefreshedAt)}
                  </time>
                </>
              ) : (
                heading
              )}
            </Box>
            <Popover
              dismissAriaLabel="Close report details"
              header="Source snapshot"
              size="large"
              triggerType="custom"
              content={
                <div className="oms-report-download__snapshot-details">
                  <SpaceBetween size="s">
                    {compact && (
                      <KeyValuePairs
                        columns={2}
                        items={[
                          {
                            label: "Source rows",
                            value: report.rowCount.toLocaleString(),
                          },
                          {
                            label: "Source size",
                            value: fileSize(report.totalBytes),
                          },
                        ]}
                      />
                    )}
                    <Box>Snapshot ID: {report.snapshotId}</Box>
                    <Box>Published: {reportTimestamp(report.exportedAt)}</Box>
                  </SpaceBetween>
                </div>
              }
            >
              <Button iconName="status-info">
                {compact ? "Report details" : "Snapshot details"}
              </Button>
            </Popover>
          </div>
        )}
        {showMetadata && !compact && (
          <KeyValuePairs
            columns={3}
            items={[
              {
                label: "Data refreshed",
                value: (
                  <time dateTime={report.sourceRefreshedAt}>
                    {reportTimestamp(report.sourceRefreshedAt)}
                  </time>
                ),
              },
              { label: "Source rows", value: report.rowCount.toLocaleString() },
              { label: "Source size", value: fileSize(report.totalBytes) },
            ]}
          />
        )}
        {newer && (
          <Alert
            type="info"
            header="A newer source report is available"
            action={
              <Button onClick={onUseLatest}>
                {accepted
                  ? "Use these settings on the latest report"
                  : "Use latest report"}
              </Button>
            }
          >
            {accepted
              ? "The existing download keeps its original source. Start a new draft to use newer data."
              : "Your settings will be checked against the latest source."}{" "}
            Newer data refreshed:{" "}
            {reportTimestamp(availableReport.sourceRefreshedAt)}.
          </Alert>
        )}
      </SpaceBetween>
    </section>
  );
}
